import * as React from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery } from '@tanstack/react-query';
import { CheckCircle2, AlertTriangle } from 'lucide-react';

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Field } from '@/components/ui/field';
import { env } from '@/lib/env';

export const Route = createFileRoute('/auth/set-password/$token')({
  staticData: { public: true },
  component: SetPasswordPage,
});

function SetPasswordPage() {
  const { token } = Route.useParams();
  const navigate = useNavigate();

  const preview = useQuery({
    queryKey: ['set-password', token],
    queryFn: async () => {
      const res = await fetch(`${env.apiBaseUrl}/api/auth/set-password/${token}`);
      if (!res.ok) {
        const j = (await res.json().catch(() => ({}))) as { error?: string };
        throw new Error(j.error ?? 'invalid');
      }
      return (await res.json()) as {
        email: string;
        full_name: string | null;
        already_set: boolean;
      };
    },
    retry: false,
  });

  const [password, setPassword] = React.useState('');
  const [confirm, setConfirm] = React.useState('');
  const [err, setErr] = React.useState<string | null>(null);

  const submit = useMutation({
    mutationFn: async () => {
      setErr(null);
      if (password.length < 8) throw new Error('Hasło musi mieć min. 8 znaków.');
      if (password !== confirm) throw new Error('Hasła nie są identyczne.');
      const res = await fetch(`${env.apiBaseUrl}/api/auth/set-password/${token}`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ password }),
      });
      if (!res.ok) {
        const j = (await res.json().catch(() => ({}))) as { error?: string };
        throw new Error(j.error ?? 'set_failed');
      }
      return res.json();
    },
    onSuccess: () => navigate({ to: '/login' as never }),
    onError: (e) => setErr((e as Error).message),
  });

  return (
    <div className="grid min-h-screen place-items-center bg-surface p-6">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>Ustaw hasło</CardTitle>
          <CardDescription>
            Witaj w pozyskajpacjenta.pl. Ustaw hasło, żeby zalogować się do panelu admin.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {preview.isPending ? (
            <p className="text-sm text-text-muted">Sprawdzam link…</p>
          ) : preview.isError ? (
            <div className="rounded-md bg-error/10 p-3 text-sm text-error ring-1 ring-error/20">
              <AlertTriangle className="mr-1 inline size-4" aria-hidden="true" />
              Link jest nieprawidłowy lub wygasł. Skontaktuj się z nami na
              kontakt@pozyskajpacjenta.pl, a wyślemy nowy.
            </div>
          ) : preview.data?.already_set ? (
            <div className="rounded-md bg-warning/10 p-3 text-sm text-warning ring-1 ring-warning/20">
              Hasło zostało już ustawione. Zaloguj się stroną{' '}
              <a href="/login" className="text-accent hover:underline">
                /login
              </a>{' '}
              albo skorzystaj z odzyskania hasła.
            </div>
          ) : preview.data ? (
            <>
              <p className="rounded-md bg-surface p-3 text-sm ring-1 ring-black/5">
                Konto: <strong>{preview.data.email}</strong>
              </p>
              <Field
                label="Hasło"
                htmlFor="password"
                hint="Min. 8 znaków. Używaj długiego, unikalnego hasła."
                required
              >
                <Input
                  id="password"
                  type="password"
                  autoComplete="new-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </Field>
              <Field label="Powtórz hasło" htmlFor="confirm" required>
                <Input
                  id="confirm"
                  type="password"
                  autoComplete="new-password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                />
              </Field>
              {err ? (
                <p className="rounded-md bg-error/10 px-3 py-2 text-sm text-error ring-1 ring-error/20">
                  {err}
                </p>
              ) : null}
              <Button
                type="button"
                className="w-full"
                onClick={() => submit.mutate()}
                disabled={password.length < 8 || password !== confirm || submit.isPending}
              >
                <CheckCircle2 className="size-4" aria-hidden="true" />
                {submit.isPending ? 'Ustawiam…' : 'Ustaw hasło i zaloguj'}
              </Button>
            </>
          ) : null}
        </CardContent>
      </Card>
    </div>
  );
}
